/**
 * TopAdContext
 *
 * Single source of truth for whether a sponsored top_bar ad (see
 * components/ads/TopAnnouncementBar.js) is currently occupying the strip
 * above the navbar. Exists so anything else that wants to render its own
 * banner near the top of the page (e.g. FreeListingPromoBar) can check
 * first and yield instead of stacking a second bar directly underneath a
 * paid ad - previously each would have fetched/rendered independently
 * with no way to know about the other.
 *
 * Wrap once in App.js. TopAnnouncementBar and FreeListingPromoBar both
 * read from here rather than hitting /api/ads/serve/?placement=top_bar
 * themselves, so there's also only ever one network request per route
 * change instead of one per consumer.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const TopAdContext = createContext({ ad: null, hasVisibleTopBarAd: false, dismissTopBarAd: () => {} });

const dismissedKey = (adId) => `topbar_dismissed_${adId}`;

export function TopAdProvider({ children }) {
  const location = useLocation();
  const [ad, setAd] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const cityMatch = location.pathname.match(/^\/businesses\/([^/]+)/);
    const citySlug = cityMatch ? cityMatch[1] : '';

    const params = new URLSearchParams({ placement: 'top_bar' });
    if (citySlug) params.set('city', citySlug);

    let cancelled = false;
    fetch(`/api/ads/serve/?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data && data.id) {
          setAd(data);
          setDismissed(sessionStorage.getItem(dismissedKey(data.id)) === '1');
        } else {
          setAd(null);
          setDismissed(false);
        }
      })
      .catch(() => {
        if (!cancelled) { setAd(null); setDismissed(false); }
      });

    return () => { cancelled = true; };
  }, [location.pathname]);

  const dismissTopBarAd = useCallback(() => {
    if (ad) sessionStorage.setItem(dismissedKey(ad.id), '1');
    setDismissed(true);
  }, [ad]);

  const value = {
    ad,
    hasVisibleTopBarAd: !!ad && !dismissed,
    dismissTopBarAd,
  };

  return (
    <TopAdContext.Provider value={value}>
      {children}
    </TopAdContext.Provider>
  );
}

export function useTopAdContext() {
  return useContext(TopAdContext);
}
